import posthog from 'posthog-js';
import { settings } from '../settings';

// TREB-220. The impersonation functions moved into orders-service's DataSupplyFunctionsWindows
// app. This flag chooses between the old standalone app and the new host so a problem in
// production is a PostHog toggle rather than a redeploy.
const NEW_ENDPOINTS_FLAG = 'impersonation-new-endpoints';

// The old host stays a literal on purpose, for the flag's lifetime only. It is the fail-safe
// target when the flag is off or PostHog has not answered yet, and the pipeline's token
// replacement only *warns* on a missing variable - so if the fallback came from config too, a
// config gap could ship an empty URL and take the fallback out with it. Removed, along with this
// flag, when the old app is decommissioned.
const LEGACY_HOST = 'https://user-impersonation-mrm.azurewebsites.net/api';
const LEGACY_IMPERSONATE_KEY = 'KaxogVzTKWDVjpdWdp2ktSqB4ZRKdRAhQsQvaa_Ix1_4AzFuEoqPUg==';
const LEGACY_RESET_KEY = 'axAD7eyiNoVgc6WzSUCdlfpGUwA86eZpYw6davINCg4kAzFufnSF2A==';

// Defaults to the old endpoints when the flag has not loaded, PostHog is unreachable, or it is
// not initialised at all (as in tests). The try/catch is a real fallback, not defensive noise:
// optional chaining covers an absent posthog, but a throwing isFeatureEnabled would otherwise
// take out the very path we roll back to.
function newEndpointsEnabled() {
  try {
    return posthog?.isFeatureEnabled?.(NEW_ENDPOINTS_FLAG) ?? false;
  } catch {
    return false;
  }
}

// The deploy-time token replacement warns rather than fails on a missing variable, so a config gap
// arrives as an empty string - and a misconfigured entry can leave the literal "#{...}#" token in
// place, which is truthy. Neither builds a working request, so both fall back to the old host.
function isConfigured(value, mustBeUrl) {
  if (!value || value.startsWith('#{')) {
    return false;
  }

  return mustBeUrl ? value.startsWith('http') : true;
}

function newHostEndpoint(functionName) {
  if (
    newEndpointsEnabled() &&
    isConfigured(settings.impersonationApiHost, true) &&
    isConfigured(settings.impersonationFunctionKey, false)
  ) {
    return `${settings.impersonationApiHost}/${functionName}?code=${settings.impersonationFunctionKey}`;
  }

  return null;
}

function endpoint(functionName, legacyKey) {
  return newHostEndpoint(functionName) ?? `${LEGACY_HOST}/${functionName}?code=${legacyKey}`;
}

export function impersonateUserEndpoint(emailToImpersonate) {
  return `${endpoint('ImpersonateUser', LEGACY_IMPERSONATE_KEY)}&user=${encodeURIComponent(emailToImpersonate)}`;
}

export function resetMeEndpoint() {
  return endpoint('ResetMe', LEGACY_RESET_KEY);
}

// Only the new host records who is being impersonated, so there is no legacy fallback: null means
// "can't tell", and the caller shows nothing.
export function impersonationStatusEndpoint() {
  return newHostEndpoint('GetImpersonationStatus');
}

// The flag decides the host, and it loads after the first render. Runs the callback once flags
// arrive (immediately if they already have). Like newEndpointsEnabled, a PostHog failure must not
// break the page, so it degrades to never calling back. Returns an unsubscribe function.
export function onEndpointFlagsLoaded(callback) {
  try {
    return posthog?.onFeatureFlags?.(callback) ?? (() => {});
  } catch {
    return () => {};
  }
}
